import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, LayersControl } from "react-leaflet";
import L from "leaflet";
import { DEFAULT_CENTER, SERVICE_LABEL, STATUS_LABEL } from "../lib/constants";
import { useBranding } from "../lib/company";

// Fix default marker icons missing in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FitBounds({ points }) {
  const map = useMap();
  // Only fit on first non-empty render or when the SET of points changes (count),
  // not on every coordinate jitter — otherwise live GPS updates yank the view
  // around every 15s and dispatchers lose their pan/zoom state.
  const fittedRef = useRef(false);
  const lastCountRef = useRef(0);
  useEffect(() => {
    if (points.length === 0) return;
    const countChanged = points.length !== lastCountRef.current;
    lastCountRef.current = points.length;
    if (fittedRef.current && !countChanged) return;
    fittedRef.current = true;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

function FocusOn({ point }) {
  const map = useMap();
  useEffect(() => {
    if (point && typeof point.lat === "number" && typeof point.lng === "number") {
      map.setView([point.lat, point.lng], 15, { animate: true });
    }
  }, [point, map]);
  return null;
}

function makeTruckIcon(status, heading, isMoving) {
  // Feb 2026: switched from circle-with-arrow to compact square markers
  // matching the Telematics Guru asset list style. Whole thing sits well
  // under the previous 32-40px footprint. Colours are green (ignition on)
  // or red (offline / ignition off). A pointed triangular tip appears on
  // top of the square and rotates with the truck's heading when moving.
  const SIZE = 14;
  const TIP = 6;
  const isOffline = status === "offline";
  const bg = isOffline ? "#DC2626" : "#10B981"; // red-600 / emerald-500
  const border = isOffline ? "#7F1D1D" : "#065F46";

  if (isMoving && !isOffline && typeof heading === "number") {
    // Container is SIZE × (SIZE + TIP): triangle at top, square below it.
    // Rotation origin lives at the centre of the square so the truck's
    // real GPS point stays fixed while the marker "swings" around it.
    return L.divIcon({
      className: "",
      html: `<div style="width:${SIZE}px;height:${SIZE + TIP}px;position:relative;transform:rotate(${heading}deg);transform-origin:${SIZE / 2}px ${TIP + SIZE / 2}px;">
               <div style="position:absolute;top:0;left:50%;transform:translateX(-50%);width:0;height:0;border-left:${SIZE / 2}px solid transparent;border-right:${SIZE / 2}px solid transparent;border-bottom:${TIP}px solid ${bg};"></div>
               <div style="position:absolute;top:${TIP}px;left:0;width:${SIZE}px;height:${SIZE}px;background:${bg};border:1px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,0.35);"></div>
             </div>`,
      iconSize: [SIZE, SIZE + TIP],
      iconAnchor: [SIZE / 2, TIP + SIZE / 2],
    });
  }

  // Stationary (or offline) — plain square, no directional tip.
  return L.divIcon({
    className: "",
    html: `<div style="width:${SIZE}px;height:${SIZE}px;background:${bg};border:1px solid ${border};box-shadow:0 1px 3px rgba(0,0,0,0.35);"></div>`,
    iconSize: [SIZE, SIZE],
    iconAnchor: [SIZE / 2, SIZE / 2],
  });
}

function makeJobIcon(label, bg) {
  return L.divIcon({
    className: "",
    html: `<div class="job-marker" style="background:${bg};width:28px;height:28px;border-radius:6px;">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function makeDropOffIcon(bg) {
  // Distinct flag-style pin so drop-off is unmistakably "destination"
  return L.divIcon({
    className: "",
    html: `<div class="job-marker" style="background:${bg};width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;">
             <span style="transform:rotate(45deg);font-weight:900;font-size:11px;color:#fff;letter-spacing:0.5px;">D</span>
           </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

const JOB_BG = {
  pending: "#F59E0B",
  assigned: "#2563EB",
  en_route: "#7C3AED",
  on_site: "#0D9488",
  completed: "#10B981",
  cancelled: "#94A3B8",
};

const hasCoord = (lat, lng) =>
  typeof lat === "number" && typeof lng === "number" && (lat !== 0 || lng !== 0);

// Tiny wrapper so we can group pickup pin + drop-off pin + polyline per job.
// React-Leaflet ignores non-Leaflet siblings, so we need Fragment, not <div>.
const FragmentForJob = ({ children }) => <>{children}</>;

export default function DispatchMap({ jobs = [], trucks = [], height = "100%", focus = null }) {
  const branding = useBranding();
  const center =
    branding?.map_center_lat != null && branding?.map_center_lng != null
      ? [branding.map_center_lat, branding.map_center_lng]
      : DEFAULT_CENTER;
  const points = [];
  jobs.forEach((j) => {
    if (hasCoord(j.lat, j.lng)) points.push([j.lat, j.lng]);
    if (hasCoord(j.drop_off_lat, j.drop_off_lng)) points.push([j.drop_off_lat, j.drop_off_lng]);
  });
  trucks.forEach((t) => {
    if (hasCoord(t.lat, t.lng)) points.push([t.lat, t.lng]);
  });

  return (
    <div style={{ height, width: "100%" }} data-testid="dispatch-map">
      <MapContainer
        center={center}
        zoom={12}
        style={{ height: "100%", width: "100%", borderRadius: 6 }}
        scrollWheelZoom
      >
        <LayersControl position="topleft">
          {/* Satellite first and checked by default: dispatchers are usually
              identifying a yard, a berm or which side of a motorway a vehicle
              is on, and imagery answers that faster than a street map. The
              other layers stay one click away. */}
          <LayersControl.BaseLayer checked name="Satellite">
            <TileLayer
              attribution='Imagery &copy; Esri, Maxar, Earthstar Geographics'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Streets">
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Topographic">
            <TileLayer
              attribution='&copy; OpenTopoMap (CC-BY-SA), SRTM'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        <FitBounds points={points} />
        <FocusOn point={focus} />

        {trucks.map((t) =>
          hasCoord(t.lat, t.lng) ? (
            <Marker
              // key includes rounded lat/lng so react-leaflet remounts the marker
              // when the truck moves (Leaflet's Marker.position is set on mount only).
              key={`t-${t.id}-${t.lat.toFixed(5)},${t.lng.toFixed(5)}-${t.heading ?? "s"}`}
              position={[t.lat, t.lng]}
              icon={makeTruckIcon(t.status, t.heading, t.is_moving)}
            >
              <Popup>
                <div className="font-display font-bold">{t.name}</div>
                <div>{t.driver}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase">{t.status} · {t.capacity}</div>
              </Popup>
            </Marker>
          ) : null
        )}

        {jobs.map((j) => {
          const jobColor = JOB_BG[j.status] || "#F59E0B";
          const pickup = hasCoord(j.lat, j.lng);
          const dropoff = hasCoord(j.drop_off_lat, j.drop_off_lng);
          return (
            <FragmentForJob key={`j-wrap-${j.id}`}>
              {pickup && (
                <Marker
                  key={`j-${j.id}-${j.lat.toFixed(5)},${j.lng.toFixed(5)}`}
                  position={[j.lat, j.lng]}
                  icon={makeJobIcon("J", jobColor)}
                >
                  <Popup>
                    <div className="font-display font-bold">{j.customer_name}</div>
                    <div className="text-xs text-slate-600">Pickup: {j.address}</div>
                    <div className="text-xs mt-1">
                      <span className="font-semibold">{SERVICE_LABEL[j.service_type]}</span>
                      {" · "}
                      <span>{STATUS_LABEL[j.status]}</span>
                    </div>
                  </Popup>
                </Marker>
              )}
              {dropoff && (
                <Marker
                  key={`j-drop-${j.id}-${j.drop_off_lat.toFixed(5)},${j.drop_off_lng.toFixed(5)}`}
                  position={[j.drop_off_lat, j.drop_off_lng]}
                  icon={makeDropOffIcon("#DC2626")}
                >
                  <Popup>
                    <div className="font-display font-bold">{j.customer_name}</div>
                    <div className="text-xs text-rose-700 font-semibold">Drop-off</div>
                    <div className="text-xs text-slate-600">{j.drop_off_address}</div>
                  </Popup>
                </Marker>
              )}
              {pickup && dropoff && (
                <Polyline
                  positions={
                    Array.isArray(j.route_geometry) && j.route_geometry.length > 1
                      ? j.route_geometry
                      : [
                          [j.lat, j.lng],
                          [j.drop_off_lat, j.drop_off_lng],
                        ]
                  }
                  pathOptions={{
                    color: jobColor,
                    weight: Array.isArray(j.route_geometry) && j.route_geometry.length > 1 ? 4 : 2,
                    opacity: Array.isArray(j.route_geometry) && j.route_geometry.length > 1 ? 0.75 : 0.55,
                    dashArray:
                      Array.isArray(j.route_geometry) && j.route_geometry.length > 1
                        ? null
                        : "6 6",
                  }}
                />
              )}
            </FragmentForJob>
          );
        })}
      </MapContainer>
    </div>
  );
}
